import { useEffect, useState } from 'react'
import toastr from 'toastr'
import { services } from '../../../services'
import ApiPlayers from '../../../api/ApiPlayers'
import ApiAvailability from '../../../api/ApiAvailability'
import featureFlags from '../../../config/featureFlags'

const getCurrentTeamId = (division) => {
  const currentPlayer = (division?.roster || []).find(
    (player) => Number(player.id) === Number(division?.player_id),
  )
  return currentPlayer?.partner?.team_id || null
}

export default function useLeagueLandingModals({
  leagueId,
  navigate,
  division,
  setDivision,
  loading,
  setLoading,
  primaryPlayer,
  setPrimaryPlayer,
  contactInfo,
}) {
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [matchToCancel, setMatchToCancel] = useState(null)
  const [isCanceling, setIsCanceling] = useState(false)
  const [staleDataWarning, setStaleDataWarning] = useState(false)
  const [showExplanationModal, setShowExplanationModal] = useState(false)
  const [showContactInfoModal, setShowContactInfoModal] = useState(false)
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false)
  const [showLandingAvailabilityModal, setShowLandingAvailabilityModal] =
    useState(false)
  const [landingAvailabilityModalVariant, setLandingAvailabilityModalVariant] =
    useState(null)
  const [editingHomeCourt, setEditingHomeCourt] = useState(false)
  const [pendingHomeCourt, setPendingHomeCourt] = useState(null)

  useEffect(() => {
    if (
      !featureFlags.REQUIRE_AVAILABILITY_FOR_PROPOSALS ||
      loading ||
      !division ||
      !division.availability_state
    ) {
      setShowLandingAvailabilityModal(false)
      setLandingAvailabilityModalVariant(null)
      return
    }

    const state = division.availability_state

    if (state.user_has_future_availability) {
      if (state.partner_needs_availability) {
        setLandingAvailabilityModalVariant('partner-soft')
        setShowLandingAvailabilityModal(true)
        return
      }

      setShowLandingAvailabilityModal(false)
      setLandingAvailabilityModalVariant(null)
      return
    }

    if (!state.user_has_ever_entered_availability) {
      setLandingAvailabilityModalVariant('hard')
      setShowLandingAvailabilityModal(true)
      return
    }

    setLandingAvailabilityModalVariant('soft')
    setShowLandingAvailabilityModal(true)
  }, [division, loading])

  useEffect(() => {
    if (!loading && primaryPlayer && !contactInfo.hasContactInfo) {
      setShowContactInfoModal(true)
    }
  }, [loading, primaryPlayer, contactInfo.hasContactInfo])

  const handleSchedulingAction = (action) => {
    if (!contactInfo.hasContactInfo) {
      setShowContactInfoModal(true)
      return
    }
    action()
  }

  const handleContactInfoComplete = async () => {
    setShowContactInfoModal(false)
    setLoading(true)

    try {
      const players = await ApiPlayers.getMyPlayers()
      const primary = players.find((player) => player.primary_player)
      if (primary) {
        setPrimaryPlayer(primary)
      }
    } catch (error) {
      console.error('Failed to reload player data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelMatchClick = (match, event) => {
    event?.stopPropagation()
    setMatchToCancel(match)
    setCancelModalOpen(true)
  }

  const handleConfirmCancel = async () => {
    if (!matchToCancel) return

    setIsCanceling(true)
    try {
      const result = await services.match.cancelMatch(matchToCancel.id)
      if (result.success) {
        toastr.success('Match canceled successfully')
        setCancelModalOpen(false)
        setMatchToCancel(null)
        setStaleDataWarning(false)

        try {
          const divisionResponse = await services.league.getMyDivision(leagueId)
          if (divisionResponse.success) {
            setDivision(divisionResponse.data)
          } else {
            console.error(
              'Failed to reload division data:',
              divisionResponse.error,
            )
            setStaleDataWarning(true)
          }
        } catch (reloadError) {
          console.error(
            'Failed to reload division data after cancellation:',
            reloadError,
          )
          setStaleDataWarning(true)
        }
      } else {
        toastr.error(result.error || 'Failed to cancel match')
      }
    } catch (error) {
      console.error('Failed to cancel match:', error)
      toastr.error('Failed to cancel match. Please try again.')
    } finally {
      setIsCanceling(false)
    }
  }

  const handleCloseModal = () => {
    if (!isCanceling) {
      setCancelModalOpen(false)
      setMatchToCancel(null)
    }
  }

  const handleRetryRefresh = async () => {
    setStaleDataWarning(false)
    try {
      const divisionResponse = await services.league.getMyDivision(leagueId)
      if (divisionResponse.success) {
        setDivision(divisionResponse.data)
      } else {
        console.error('Retry refresh failed:', divisionResponse.error)
        toastr.error(
          'Failed to refresh data. Please try again or reload the page.',
        )
        setStaleDataWarning(true)
      }
    } catch (error) {
      console.error('Retry refresh failed:', error)
      toastr.error(
        'Failed to refresh data. Please try again or reload the page.',
      )
      setStaleDataWarning(true)
    }
  }

  const handleAddAvailability = () => {
    setAvailabilityModalOpen(true)
  }

  const handleAvailabilitySave = async (availabilityData) => {
    try {
      const isTeamScoped = availabilityData.apply_to === 'team'
      const result = isTeamScoped
        ? await ApiAvailability.setTeamAvailability({
            ...availabilityData,
            team_id: availabilityData.team_id || getCurrentTeamId(division),
          })
        : await ApiAvailability.create(availabilityData)

      if (result.success) {
        toastr.success(
          isTeamScoped
            ? 'Team availability updated.'
            : 'Your availability saved.',
        )
        setAvailabilityModalOpen(false)
      } else {
        toastr.error(result.error || 'Failed to add availability')
      }
    } catch (error) {
      console.error('Error saving availability:', error)
      toastr.error('An error occurred while adding availability')
    }
  }

  const handleEnterAvailability = () => {
    if (!leagueId || isNaN(Number(leagueId))) {
      console.error('Invalid leagueId for navigation:', leagueId)
      navigate('/')
      return
    }

    navigate(`/leagues/${leagueId}/schedule-month`, {
      state: { openAvailabilityModal: true },
    })
  }

  const dismissStaleDataWarning = () => setStaleDataWarning(false)
  const handleGoHome = () => navigate('/')
  const handleDismissLandingModal = () => setShowLandingAvailabilityModal(false)
  const openExplanationModal = () => setShowExplanationModal(true)
  const closeExplanationModal = () => setShowExplanationModal(false)

  return {
    cancelModalOpen,
    matchToCancel,
    isCanceling,
    staleDataWarning,
    showExplanationModal,
    showContactInfoModal,
    setShowContactInfoModal,
    availabilityModalOpen,
    setAvailabilityModalOpen,
    showLandingAvailabilityModal,
    landingAvailabilityModalVariant,
    editingHomeCourt,
    setEditingHomeCourt,
    pendingHomeCourt,
    setPendingHomeCourt,
    handleSchedulingAction,
    handleContactInfoComplete,
    handleCancelMatchClick,
    handleConfirmCancel,
    handleCloseModal,
    handleRetryRefresh,
    dismissStaleDataWarning,
    handleAddAvailability,
    handleAvailabilitySave,
    handleEnterAvailability,
    handleGoHome,
    handleDismissLandingModal,
    openExplanationModal,
    closeExplanationModal,
  }
}
